import api from "@/shared/api/api";
import type {
  CreatorBankAccount,
  UpdateCreatorBankAccountPayload,
  CreateCreatorBankAccountPayload,
  GetCreatorReceivableSnapshotsParams,
  PaginatedCreatorReceivableSnapshotsResponse,
  GetCreatorPaymentsParams,
  PaginatedCreatorPaymentsResponse,
} from "../types";
import { isCreatorFinancialMock } from "../config";
import {
  getMockBankAccounts,
  getMockReceivableSnapshots,
  getMockPayments,
  mockBankAccounts,
} from "./mockData";

const BASE = "/v1/creator/me";

export const creatorFinancialApi = {
  bankAccounts: {
    list: async (signal?: AbortSignal): Promise<CreatorBankAccount[]> => {
      if (isCreatorFinancialMock) {
        return getMockBankAccounts();
      }
      const res = await api<{ data?: CreatorBankAccount[] } | CreatorBankAccount[]>({
        url: `${BASE}/bank-accounts`,
        method: "GET",
        signal,
      });
      return Array.isArray(res) ? res : (res.data ?? []);
    },
    create: async (
      payload: CreateCreatorBankAccountPayload,
      signal?: AbortSignal
    ): Promise<CreatorBankAccount> => {
      if (isCreatorFinancialMock) {
        const newAccount: CreatorBankAccount = {
          id: `mock-bank-${Date.now()}`,
          ...payload,
          active: true,
          createdAt: new Date().toISOString(),
          lastUpdatedAt: new Date().toISOString(),
        };
        mockBankAccounts.push(newAccount);
        return newAccount;
      }
      return api<CreatorBankAccount>({
        url: `${BASE}/bank-accounts`,
        method: "POST",
        data: payload,
        signal,
      });
    },
    update: async (
      id: string,
      payload: UpdateCreatorBankAccountPayload,
      signal?: AbortSignal
    ): Promise<CreatorBankAccount> => {
      if (isCreatorFinancialMock) {
        const account = mockBankAccounts.find((a) => a.id === id);
        if (!account) throw new Error("Conta não encontrada");
        return { ...account, ...payload, lastUpdatedAt: new Date().toISOString() };
      }
      return api<CreatorBankAccount>({
        url: `${BASE}/bank-accounts/${id}`,
        method: "PUT",
        data: payload,
        signal,
      });
    },
  },

  receivableSnapshots: {
    list: async (
      params?: GetCreatorReceivableSnapshotsParams,
      signal?: AbortSignal
    ): Promise<PaginatedCreatorReceivableSnapshotsResponse> => {
      if (isCreatorFinancialMock) {
        return getMockReceivableSnapshots();
      }
      const query: Record<string, unknown> = {};
      if (params?.page !== undefined) query.page = params.page;
      if (params?.limit !== undefined) query.limit = params.limit;
      if (params?.periodMonths !== undefined) query.periodMonths = params.periodMonths;
      const res = await api<PaginatedCreatorReceivableSnapshotsResponse>({
        url: `${BASE}/receivable-snapshots`,
        method: "GET",
        params: Object.keys(query).length ? query : undefined,
        signal,
      });
      return {
        data: res.data ?? [],
        page: res.page ?? 1,
        limit: res.limit ?? 50,
        totalItems: res.totalItems ?? 0,
        totalPage: res.totalPage ?? 1,
      };
    },
  },

  payments: {
    list: async (
      params?: GetCreatorPaymentsParams,
      signal?: AbortSignal
    ): Promise<PaginatedCreatorPaymentsResponse> => {
      if (isCreatorFinancialMock) {
        return getMockPayments({
          page: params?.page,
          limit: params?.limit,
          status: params?.status,
        });
      }
      const query: Record<string, unknown> = {};
      if (params?.page !== undefined) query.page = params.page;
      if (params?.limit !== undefined) query.limit = params.limit;
      if (params?.status && params.status !== "all") query.status = params.status;
      if (params?.search) query.search = params.search;
      const res = await api<PaginatedCreatorPaymentsResponse>({
        url: `${BASE}/payments`,
        method: "GET",
        params: Object.keys(query).length ? query : undefined,
        signal,
      });
      return {
        data: res.data ?? [],
        page: res.page ?? 1,
        limit: res.limit ?? 10,
        totalItems: res.totalItems ?? 0,
        totalPage: res.totalPage ?? 1,
      };
    },
  },
};
